import { CircularProgress } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText, { ListItemTextProps } from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import { useDashboardContext } from '@shared/contexts/DashboardContext';
import { PropsWithChildren, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface CustomListItemProps extends ListItemTextProps {
  loading?: boolean;
  onClick: () => void;
}

type CustomListItemWithActionsProps = PropsWithChildren & {
  dashboardId: number;
  label: string;
  onClick?: () => void;
};

type DashboardListItemProps = {
  dashboardId: number;
  label: string;
  onClick: () => void;
  onDelete: () => void;
  onEdit: () => void;
  onShare: () => void;
};

type SharedDashboardListItemProps = {
  dashboardId: number;
  label: string;
  onAccept: () => void;
  onReject: () => void;
};

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  paddingLeft: '8px',
  paddingRight: '90px!important', //30px each action icon
}));

const StyledIconButton = styled(IconButton)(() => ({
  padding: '6px',
  '& i': {
    fontSize: '18px',
  },
}));

function CustomListItem({ onClick, loading, ...listItemProps }: CustomListItemProps) {
  return (
    <ListItem disablePadding secondaryAction={loading && <CircularProgress size={20} />}>
      <StyledListItemButton onClick={onClick}>
        <ListItemText {...listItemProps} />
      </StyledListItemButton>
    </ListItem>
  );
}

function CustomListItemWithActions({ dashboardId, label, children, onClick }: CustomListItemWithActionsProps) {
  const textRef = useRef<HTMLSpanElement | null>(null);

  const { currentDashboard } = useDashboardContext();
  const location = useLocation();
  const isCurrent = currentDashboard?.id === dashboardId && location.pathname.endsWith(currentDashboard?.id.toString());

  return (
    <ListItem disablePadding secondaryAction={children} sx={{ '& .MuiListItemSecondaryAction-root': { right: '0px' } }}>
      <StyledListItemButton
        onClick={onClick}
        disabled={isCurrent}
        sx={{
          opacity: '0.7',
          '&.Mui-disabled': {
            opacity: 1,
            backgroundColor: '#373f4a',
          },
        }}
      >
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            ref: textRef,
            style: {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}
        />
      </StyledListItemButton>
    </ListItem>
  );
}

function DashboardListItem({ dashboardId, label, onClick, onDelete, onEdit, onShare }: DashboardListItemProps) {
  const { allDashboards } = useDashboardContext();
  const isDeleteHidden = allDashboards.length === 1;

  return (
    <CustomListItemWithActions dashboardId={dashboardId} label={label} onClick={onClick}>
      <Stack direction="row">
        <StyledIconButton onClick={onEdit}>
          <i className="ri-edit-box-line" />
        </StyledIconButton>
        <StyledIconButton onClick={onShare}>
          <i className="ri-share-line" />
        </StyledIconButton>
        {!isDeleteHidden && (
          <StyledIconButton onClick={onDelete}>
            <i className="ri-delete-bin-line" />
          </StyledIconButton>
        )}
      </Stack>
    </CustomListItemWithActions>
  );
}

function SharedDashboardListItem({ dashboardId, label, onAccept, onReject }: SharedDashboardListItemProps) {
  return (
    <CustomListItemWithActions dashboardId={dashboardId} label={label}>
      <Stack direction="row">
        <StyledIconButton onClick={onAccept}>
          <i className="ri-checkbox-circle-line" />
        </StyledIconButton>
        <StyledIconButton onClick={onReject}>
          <i className="ri-close-circle-line" />
        </StyledIconButton>
      </Stack>
    </CustomListItemWithActions>
  );
}

export { CustomListItem, DashboardListItem, SharedDashboardListItem };
